import React, { useState, useEffect, useContext } from "react";

import {
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiChevronRight,
  FiAlertTriangle,
  FiRefreshCw,
  FiBarChart2,
  FiArrowLeft,
  FiList,
  FiTag,
  FiFileText,
  FiSlash,
} from "react-icons/fi";

import { BsShop, BsPersonCircle } from "react-icons/bs";

import Swal from "sweetalert2";

import "../../../assets/css/admin/shopmanagement/shopmanagementInAdmin.css";

import AdminMasterLayout from "../../../layout/admin/AdminMasterLayout";

import AppContext from "../../../context/AppContext";

import axios from "axios";


// ============================================================
// STATUS BADGE
// ============================================================

const StatusBadge = ({ status }) => {

  const map = {
    approved: "badge-approved",
    pending: "badge-pending",
    rejected: "badge-rejected",

    active: "badge-approved",
    blocked: "badge-rejected",

    ongoing: "badge-ongoing",
    completed: "badge-completed",
    cancelled: "badge-cancelled",
  };

  return (
    <span className={`badge ${map[status] || "badge-pending"}`}>
      {status || "N/A"}
    </span>
  );
};


// ============================================================
// SHOP LIST PAGE
// ============================================================

const ShopListPage = ({
  shops,
  loading,
  onViewShop,
  onViewOrders,
  onApprove,
  onReject,
  onBlockShop,
  onReactivateShop,
}) => {

  const [activeTab, setActiveTab] = useState("all");


  // ==========================================================
  // COUNTS
  // ==========================================================

  const verifiedCount = shops.filter(
    (shop) => shop.kyc_status === "approved"
  ).length;

  const pendingCount = shops.filter(
    (shop) => shop.kyc_status === "pending"
  ).length;

  const blockedCount = shops.filter(
    (shop) => shop.status === "blocked"
  ).length;


  // ==========================================================
  // FILTER
  // ==========================================================

  const filteredShops = shops.filter((shop) => {

    if (activeTab === "verified") {
      return shop.kyc_status === "approved";
    }

    if (activeTab === "pending") {
      return shop.kyc_status === "pending";
    }

    if (activeTab === "blocked") {
      return shop.status === "blocked";
    }

    return true;
  });


  // ==========================================================
  // APPROVE KYC
  // ==========================================================

  const handleApproveClick = (shop, e) => {

    e.stopPropagation();

    Swal.fire({
      title: `Approve "${shop.businessName || shop.fullName}"?`,

      text: "This shop's KYC will be marked as approved.",

      icon: "question",

      showCancelButton: true,

      confirmButtonColor: "#0abf8a",

      cancelButtonColor: "#94a3b8",

      confirmButtonText: "Yes, Approve",

    }).then((result) => {

      if (result.isConfirmed) {
        onApprove(shop);
      }

    });
  };


  // ==========================================================
  // REJECT KYC
  // ==========================================================

  const handleRejectClick = (shop, e) => {

    e.stopPropagation();

    Swal.fire({

      title: `Reject "${shop.businessName || shop.fullName}"?`,

      text: "Please provide a reason for rejection.",

      icon: "warning",

      input: "textarea",

      inputPlaceholder:
        "Enter rejection reason...",

      showCancelButton: true,

      confirmButtonColor: "#ff5c5c",

      cancelButtonColor: "#94a3b8",

      confirmButtonText: "Yes, Reject",

      inputValidator: (value) => {

        if (!value || !value.trim()) {
          return "Rejection reason is required!";
        }

      },

    }).then((result) => {

      if (result.isConfirmed) {

        onReject(
          shop,
          result.value.trim()
        );

      }

    });
  };


  // ==========================================================
  // BLOCK SHOP
  // ==========================================================

  const handleBlockClick = (shop, e) => {

    e.stopPropagation();

    Swal.fire({

      title:
        `Block "${shop.businessName || shop.fullName}"?`,

      text:
        "The shop will be blocked until you reactivate it.",

      icon: "warning",

      input: "textarea",

      inputPlaceholder:
        "Enter complaint / reason for blocking...",

      showCancelButton: true,

      confirmButtonColor: "#dc3545",

      cancelButtonColor: "#94a3b8",

      confirmButtonText: "Block Shop",

      inputValidator: (value) => {

        if (!value || !value.trim()) {
          return "Block reason is required!";
        }

      },

    }).then((result) => {

      if (result.isConfirmed) {

        onBlockShop(
          shop,
          result.value.trim()
        );

      }

    });
  };


  // ==========================================================
  // REACTIVATE SHOP
  // ==========================================================

  const handleReactivateClick = (shop, e) => {

    e.stopPropagation();

    Swal.fire({

      title:
        `Reactivate "${shop.businessName || shop.fullName}"?`,

      text:
        "The shop will become active again.",

      icon: "question",

      showCancelButton: true,

      confirmButtonColor: "#0abf8a",

      cancelButtonColor: "#94a3b8",

      confirmButtonText: "Reactivate Shop",

    }).then((result) => {

      if (result.isConfirmed) {

        onReactivateShop(shop);

      }

    });
  };


  return (

    <AdminMasterLayout>

      <div className="admin-layout">

        <div className="page-body">


          {/* ==================================================
              FILTER TABS
          ================================================== */}

          <div className="filter-tabs">

            <button
              className={`filter-tab ${
                activeTab === "all" ? "active" : ""
              }`}
              onClick={() => setActiveTab("all")}
            >
              All Shops

              <span className="tab-count">
                {shops.length}
              </span>

            </button>


            <button
              className={`filter-tab ${
                activeTab === "verified" ? "active" : ""
              }`}
              onClick={() => setActiveTab("verified")}
            >
              Verified

              <span className="tab-count">
                {verifiedCount}
              </span>

            </button>


            <button
              className={`filter-tab ${
                activeTab === "pending" ? "active" : ""
              }`}
              onClick={() => setActiveTab("pending")}
            >
              Pending Verification

              <span className="tab-count">
                {pendingCount}
              </span>

            </button>


            <button
              className={`filter-tab ${
                activeTab === "blocked" ? "active" : ""
              }`}
              onClick={() => setActiveTab("blocked")}
            >
              Blocked

              <span className="tab-count">
                {blockedCount}
              </span>

            </button>

          </div>


          {/* ==================================================
              TABLE
          ================================================== */}

          <div className="table-card">

            <div className="table-card-header">

              <div>

                <h2>
                  All Registered Shops
                </h2>

                <p>
                  Manage and monitor all shops on the platform
                </p>

              </div>

            </div>


            <div className="table-wrapper">

              <table className="data-table">

                <thead>

                  <tr>

                    <th>Shop</th>

                    <th>Owner</th>

                    <th>Deals</th>

                    <th>Revenue</th>

                    <th>Status</th>

                    <th>KYC Status</th>

                    <th>Actions</th>

                  </tr>

                </thead>


                <tbody>

                  {loading ? (

                    <tr>

                      <td colSpan={7}>

                        <div className="empty-state">

                          <h3>
                            Loading shops...
                          </h3>

                        </div>

                      </td>

                    </tr>

                  ) : filteredShops.length === 0 ? (

                    <tr>

                      <td colSpan={7}>

                        <div className="empty-state">

                          <div className="empty-icon">
                            🏪
                          </div>

                          <h3>
                            No shops found
                          </h3>

                          <p>
                            No shops match the current filter.
                          </p>

                        </div>

                      </td>

                    </tr>

                  ) : (

                    filteredShops.map((shop) => (

                      <tr key={shop.id}>


                        {/* SHOP */}

                        <td>

                          <div className="shop-name-cell">

                            <div
                              className={`shop-icon ${
                                shop.colorClass || "color-1"
                              }`}
                            >
                              {shop.businessName?.charAt(0)}
                            </div>

                            <span className="shop-name-text">
                              {shop.businessName}
                            </span>

                          </div>

                        </td>


                        {/* OWNER */}

                        <td>

                          <div className="owner-cell">

                            <div className="owner-avatar">

                              {shop.fullName
                                ?.split(" ")
                                .map((name) => name[0])
                                .join("")}

                            </div>

                            {shop.fullName}

                          </div>

                        </td>


                        {/* DEALS */}

                        <td>

                          <span
                            className={
                              !shop.tpdeal?.length
                                ? "deals-count deals-zero"
                                : "deals-count"
                            }
                          >
                            {shop.tpdeal?.length || 0}
                          </span>

                        </td>


                        {/* REVENUE */}

                        <td>

                          <span
                            className={
                              !shop.revenue
                                ? "revenue-amount revenue-zero"
                                : "revenue-amount"
                            }
                          >

                            PKR{" "}
                            {Number(
                              shop.revenue || 0
                            ).toLocaleString()}

                          </span>

                        </td>


                        {/* SHOP STATUS */}

                        <td>

                          <StatusBadge
                            status={shop.status}
                          />

                        </td>


                        {/* KYC */}

                        <td>

                          <StatusBadge
                            status={shop.kyc_status}
                          />

                        </td>


                        {/* ==================================================
                            ACTIONS
                        ================================================== */}

                        <td>

                          <div className="action-btns">


                            {/* VIEW */}

                            <button
                              className="action-btn view"
                              data-tooltip="View Details"
                              onClick={() =>
                                onViewShop(shop)
                              }
                            >
                              <FiEye size={14} />
                            </button>


                            {/* ORDERS */}

                            <button
                              className="action-btn view"
                              data-tooltip="View Orders"
                              onClick={() =>
                                onViewOrders(shop)
                              }
                            >
                              <FiList size={14} />
                            </button>


                            {/* ==================================================
                                PENDING KYC
                            ================================================== */}

                            {shop.kyc_status === "pending" && (
                              <>

                                <button
                                  className="action-btn approve"
                                  data-tooltip="Approve KYC"
                                  onClick={(e) =>
                                    handleApproveClick(
                                      shop,
                                      e
                                    )
                                  }
                                >
                                  <FiCheckCircle size={14} />
                                </button>


                                <button
                                  className="action-btn reject"
                                  data-tooltip="Reject KYC"
                                  onClick={(e) =>
                                    handleRejectClick(
                                      shop,
                                      e
                                    )
                                  }
                                >
                                  <FiXCircle size={14} />
                                </button>

                              </>
                            )}


                            {/* ==================================================
                                APPROVED SHOP
                                ACTIVE = BLOCK
                                BLOCKED = REACTIVATE
                            ================================================== */}

                            {shop.kyc_status === "approved" && (

                              shop.status === "blocked" ? (

                                <button
                                  className="action-btn approve"
                                  data-tooltip="Reactivate Shop"
                                  onClick={(e) =>
                                    handleReactivateClick(
                                      shop,
                                      e
                                    )
                                  }
                                >
                                  <FiRefreshCw size={14} />
                                </button>

                              ) : (

                                <button
                                  className="action-btn reject"
                                  data-tooltip="Block Shop"
                                  onClick={(e) =>
                                    handleBlockClick(
                                      shop,
                                      e
                                    )
                                  }
                                >
                                  <FiSlash size={14} />
                                </button>

                              )

                            )}

                          </div>

                        </td>

                      </tr>

                    ))

                  )}

                </tbody>

              </table>

            </div>

          </div>

        </div>

      </div>

    </AdminMasterLayout>
  );
};


// ============================================================
// SHOP DETAIL PAGE
// ============================================================

const ShopDetailPage = ({
  shop,
  onBack,
  onViewOrders,
  onApprove,
  onReject,
  onBlockShop,
  onReactivateShop,
}) => {


  // ==========================================================
  // APPROVE KYC
  // ==========================================================

  const handleApproveKyc = () => {

    Swal.fire({

      title: "Approve KYC?",

      text:
        `Approve KYC verification for ${shop.businessName}?`,

      icon: "question",

      showCancelButton: true,

      confirmButtonColor: "#0abf8a",

      cancelButtonColor: "#94a3b8",

      confirmButtonText: "Approve KYC",

    }).then((result) => {

      if (result.isConfirmed) {
        onApprove(shop);
      }

    });
  };


  // ==========================================================
  // REJECT KYC
  // ==========================================================

  const handleRejectKyc = () => {

    Swal.fire({

      title: "Reject KYC?",

      text:
        "This will reject the shop KYC documents.",

      icon: "warning",

      showCancelButton: true,

      confirmButtonColor: "#ff5c5c",

      cancelButtonColor: "#94a3b8",

      confirmButtonText: "Reject",

    }).then((result) => {

      if (result.isConfirmed) {
        onReject(shop);
      }

    });
  };


  // ==========================================================
  // BLOCK FROM DETAIL
  // ==========================================================

  const handleBlockClick = () => {

    Swal.fire({

      title:
        `Block "${shop.businessName}"?`,

      text:
        "Enter the reason for blocking this shop.",

      icon: "warning",

      input: "textarea",

      inputPlaceholder:
        "Complaint / issue / reason...",

      showCancelButton: true,

      confirmButtonColor: "#dc3545",

      cancelButtonColor: "#94a3b8",

      confirmButtonText: "Block Shop",

      inputValidator: (value) => {

        if (!value || !value.trim()) {
          return "Block reason is required!";
        }

      },

    }).then((result) => {

      if (result.isConfirmed) {

        onBlockShop(
          shop,
          result.value.trim()
        );

      }

    });
  };


  // ==========================================================
  // REACTIVATE FROM DETAIL
  // ==========================================================

  const handleReactivateClick = () => {

    Swal.fire({

      title:
        `Reactivate "${shop.businessName}"?`,

      text:
        "This shop will become active again.",

      icon: "question",

      showCancelButton: true,

      confirmButtonColor: "#0abf8a",

      cancelButtonColor: "#94a3b8",

      confirmButtonText: "Reactivate",

    }).then((result) => {

      if (result.isConfirmed) {
        onReactivateShop(shop);
      }

    });
  };


  // ==========================================================
  // VIEW KYC DOCUMENT
  // ==========================================================

  const handleViewDoc = () => {

    if (!shop.kyc_document) {

      Swal.fire({
        icon: "info",
        title: "No Document",
        text: "No KYC document uploaded.",
      });

      return;
    }


    Swal.fire({

      title: "KYC Document",

      html: `
        <div style="padding:20px;">
          <div style="font-size:55px;margin-bottom:10px;">
            📄
          </div>

          <strong>
            ${shop.kyc_document}
          </strong>
        </div>
      `,

      confirmButtonColor: "#0f4c81",

    });
  };


  // ==========================================================
  // STATS
  // ==========================================================

  const statCards = [

    {
      icon: <BsShop />,
      label: "Shop Name",
      value: shop?.businessName,
      colorClass: "blue",
    },

    {
      icon: <BsPersonCircle />,
      label: "Owner",
      value: shop?.fullName,
      colorClass: "purple",
    },

    {
      icon: <FiTag />,
      label: "Total Deals",
      value: shop?.tpdeal?.length || 0,
      colorClass: "orange",
    },

    {
      icon: <FiBarChart2 />,
      label: "Revenue",
      value: `PKR ${Number(
        shop?.revenue || 0
      ).toLocaleString()}`,
      colorClass: "green",
    },

  ];


  const recentOrders =
    shop?.tporders?.slice(0, 5) || [];


  return (

    <div className="admin-layout">

      <div className="page-body">


        {/* ==================================================
            HEADER
        ================================================== */}

        <div className="page-header">

          <div className="page-header-left">

            <h1>
              {shop.businessName}
            </h1>

            <p>
              Shop details and management
            </p>

          </div>


          <button
            className="back-btn"
            onClick={onBack}
          >

            <FiArrowLeft size={15} />

            Back to Shops

          </button>

        </div>


        {/* ==================================================
            STATS
        ================================================== */}

        <div className="stats-row">

          {statCards.map((card, index) => (

            <div
              className="stat-card"
              key={index}
            >

              <div
                className={`stat-icon ${card.colorClass}`}
              >
                {card.icon}
              </div>

              <div className="stat-info">

                <div className="label">
                  {card.label}
                </div>

                <div className="value">
                  {card.value}
                </div>

              </div>

            </div>

          ))}

        </div>


        {/* ==================================================
            SHOP STATUS
        ================================================== */}

        <div className="info-card">

          <div className="info-card-header">

            <div className="icon">
              <BsShop />
            </div>

            <h3>
              Shop Status
            </h3>

          </div>


          <div className="info-card-body">


            <div className="info-row">

              <span className="info-label">
                Current Status
              </span>

              <span className="info-value">

                <StatusBadge
                  status={shop.status}
                />

              </span>

            </div>


            {shop.status === "blocked" &&
              shop.block_reason && (

                <div className="info-row">

                  <span className="info-label">
                    Block Reason
                  </span>

                  <span className="info-value">
                    {shop.block_reason}
                  </span>

                </div>

              )}


            {/* ==================================================
                ACTIVE -> BLOCK
                BLOCKED -> REACTIVATE
            ================================================== */}

            {shop.kyc_status === "approved" && (

              <div
                style={{
                  marginTop: "18px",
                  display: "flex",
                  gap: "10px",
                }}
              >

                {shop.status === "blocked" ? (

                  <button
                    className="kyc-approve-btn"
                    onClick={
                      handleReactivateClick
                    }
                  >

                    <FiRefreshCw size={15} />

                    Reactivate Shop

                  </button>

                ) : (

                  <button
                    className="kyc-reject-btn"
                    onClick={
                      handleBlockClick
                    }
                  >

                    <FiSlash size={15} />

                    Block Shop

                  </button>

                )}

              </div>

            )}

          </div>

        </div>


        {/* ==================================================
            SHOP INFORMATION
        ================================================== */}

        <div className="detail-grid">


          <div className="info-card">

            <div className="info-card-header">

              <div className="icon">
                <FiFileText />
              </div>

              <h3>
                Shop Information
              </h3>

            </div>


            <div className="info-card-body">


              <div className="info-row">

                <span className="info-label">
                  KYC Status
                </span>

                <span className="info-value">

                  <StatusBadge
                    status={shop.kyc_status}
                  />

                </span>

              </div>


              <div className="info-row">

                <span className="info-label">
                  Shop Status
                </span>

                <span className="info-value">

                  <StatusBadge
                    status={shop.status}
                  />

                </span>

              </div>


              <div className="info-row">

                <span className="info-label">
                  Business Name
                </span>

                <span className="info-value">
                  {shop.businessName || "N/A"}
                </span>

              </div>


              <div className="info-row">

                <span className="info-label">
                  Owner
                </span>

                <span className="info-value">
                  {shop.fullName || "N/A"}
                </span>

              </div>


              <div className="info-row">

                <span className="info-label">
                  Email
                </span>

                <span className="info-value">
                  {shop.email || "N/A"}
                </span>

              </div>


              <div className="info-row">

                <span className="info-label">
                  Phone
                </span>

                <span className="info-value">
                  {shop.phone || "N/A"}
                </span>

              </div>


              <div className="info-row">

                <span className="info-label">
                  Business Category
                </span>

                <span className="info-value">
                  {shop.businessCategory || "N/A"}
                </span>

              </div>


              <div className="info-row">

                <span className="info-label">
                  Address
                </span>

                <span className="info-value">
                  {shop.businessAddress || "N/A"}
                </span>

              </div>


              {shop.status === "blocked" &&
                shop.block_reason && (

                  <div className="info-row">

                    <span className="info-label">
                      Block Reason
                    </span>

                    <span className="info-value">
                      {shop.block_reason}
                    </span>

                  </div>

                )}


              {shop.kyc_document && (

                <div className="info-row">

                  <span className="info-label">
                    KYC Document
                  </span>

                  <span
                    className="info-value"
                    style={{
                      color: "#0f4c81",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={handleViewDoc}
                  >

                    <FiFileText size={14} />

                    {" "}

                    {shop.kyc_document}

                  </span>

                </div>

              )}

            </div>

          </div>


          {/* ==================================================
              KYC PANEL
          ================================================== */}

          {shop.kyc_status === "pending" && (

            <div className="kyc-panel">

              <div className="kyc-header">

                <div className="kyc-icon">
                  <FiAlertTriangle />
                </div>

                <h3>
                  KYC Verification Pending
                </h3>

              </div>


              <div className="kyc-body">

                <p className="kyc-desc">

                  Review the submitted documents
                  before approving this shop.

                </p>


                {shop.kyc_document && (

                  <div className="kyc-doc-item">

                    <div className="kyc-doc-info">

                      <div className="kyc-doc-icon">
                        <FiFileText />
                      </div>

                      <div className="kyc-doc-name">
                        {shop.kyc_document}
                      </div>

                    </div>


                    <div className="kyc-doc-actions">

                      <button
                        className="kyc-action-btn view-btn"
                        onClick={handleViewDoc}
                      >

                        <FiEye size={13} />

                        View

                      </button>

                    </div>

                  </div>

                )}


                <div className="kyc-footer">

                  <button
                    className="kyc-approve-btn"
                    onClick={handleApproveKyc}
                  >

                    <FiCheckCircle size={15} />

                    Approve KYC

                  </button>


                  <button
                    className="kyc-reject-btn"
                    onClick={handleRejectKyc}
                  >

                    <FiXCircle size={15} />

                    Reject

                  </button>

                </div>

              </div>

            </div>

          )}

        </div>


        {/* ==================================================
            RECENT ORDERS
        ================================================== */}

        <div className="orders-section">

          <div className="orders-section-header">

            <h3>

              <span className="header-icon">
                <FiList />
              </span>

              Recent Orders

            </h3>


            {shop?.tporders?.length > 0 && (

              <button
                className="view-all-btn"
                onClick={() =>
                  onViewOrders(shop)
                }
              >

                View All

                <FiChevronRight size={13} />

              </button>

            )}

          </div>


          {recentOrders.length === 0 ? (

            <div className="empty-state">

              <div className="empty-icon">
                📦
              </div>

              <h3>
                No orders yet
              </h3>

              <p>
                This shop hasn't received any orders.
              </p>

            </div>

          ) : (

            <div className="table-wrapper">

              <table className="data-table">

                <thead>

                  <tr>

                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Deal</th>
                    <th>Amount</th>
                    <th>Status</th>

                  </tr>

                </thead>


                <tbody>

                  {recentOrders.map((order) => (

                    <tr key={order.id}>

                      <td>
                        {order.id}
                      </td>

                      <td>

                        <div className="owner-cell">

                          <div className="owner-avatar">

                            {order.tpcustomer?.fullName
                              ?.split(" ")
                              ?.map(
                                (name) =>
                                  name[0]
                              )
                              ?.join("")}

                          </div>

                          {order.tpcustomer?.fullName ||
                            "No Customer"}

                        </div>

                      </td>

                      <td>

                        {order.tpdeal?.deal_title ||
                          "No Deal"}

                      </td>

                      <td>

                        PKR{" "}
                        {Number(
                          order.amount || 0
                        ).toLocaleString()}

                      </td>

                      <td>

                        <StatusBadge
                          status={order.status}
                        />

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

          )}

        </div>

      </div>

    </div>

  );
};


// ============================================================
// ORDERS PAGE
// ============================================================

const OrdersListPage = ({
  shop,
  onBack,
}) => {

  const orders =
    shop?.tporders?.filter(
      (order) =>
        order.status === "ongoing" ||
        order.status === "completed"
    ) || [];


  return (

    <AdminMasterLayout>

      <div className="admin-layout">

        <div className="page-body">


          <div className="page-header">

            <div>

              <h1>
                {shop.businessName} Orders
              </h1>

              <p>
                Orders for {shop.businessName}
              </p>

            </div>


            <button
              className="back-btn"
              onClick={onBack}
            >

              <FiArrowLeft size={15} />

              Back

            </button>

          </div>


          <div className="orders-section">

            <div className="orders-section-header">

              <h3>

                <span className="header-icon">
                  <FiList />
                </span>

                All Orders

              </h3>

              <span className="view-all-btn">
                {orders.length} orders
              </span>

            </div>


            {orders.length === 0 ? (

              <div className="empty-state">

                <div className="empty-icon">
                  📭
                </div>

                <h3>
                  No orders
                </h3>

                <p>
                  This shop has no active
                  or completed orders.
                </p>

              </div>

            ) : (

              <div className="table-wrapper">

                <table className="data-table">

                  <thead>

                    <tr>

                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Status</th>

                    </tr>

                  </thead>


                  <tbody>

                    {orders.map((order) => (

                      <tr key={order.id}>

                        <td>
                          {order.id}
                        </td>

                        <td>

                          {order.tpcustomer?.fullName ||
                            "No Customer"}

                        </td>

                        <td>

                          PKR{" "}
                          {Number(
                            order.amount || 0
                          ).toLocaleString()}

                        </td>

                        <td>

                          {order.created_at
                            ? new Date(
                                order.created_at
                              ).toLocaleDateString(
                                "en-GB"
                              )
                            : "N/A"}

                        </td>

                        <td>

                          <StatusBadge
                            status={order.status}
                          />

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

            )}

          </div>

        </div>

      </div>

    </AdminMasterLayout>
  );
};


// ============================================================
// MAIN COMPONENT
// ============================================================

const ShopManagementInAdmin = () => {

  const [view, setView] =
    useState("list");

  const [selectedShop, setSelectedShop] =
    useState(null);

  const [shops, setShops] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const { baseUrl } =
    useContext(AppContext);


  // ==========================================================
  // FETCH SHOPS
  // ==========================================================

  const fetchAllShops = () => {

    setLoading(true);

    axios

      .get(
        `${baseUrl}/admin/shops/fetchAllShops`
      )

      .then((res) => {

        console.log(
          "Shop API Response:",
          res.data
        );

        setShops(
          res.data.shops || []
        );

      })

      .catch((error) => {

        console.log(
          "Fetch Shops Error:",
          error
        );

        Swal.fire({

          icon: "error",

          title: "Error",

          text:
            error.response?.data?.message ||
            "Unable to fetch shops.",

        });

      })

      .finally(() => {

        setLoading(false);

      });
  };


  useEffect(() => {

    fetchAllShops();

  }, []);


  // ==========================================================
  // VIEW SHOP
  // ==========================================================

  const handleViewShop = (shop) => {

    setSelectedShop(shop);

    setView("detail");
  };


  // ==========================================================
  // VIEW ORDERS
  // ==========================================================

  const handleViewOrders = (shop) => {

    setSelectedShop(shop);

    setView("orders");
  };


  // ==========================================================
  // KYC UPDATE
  // ==========================================================

  const handleKycStatusUpdate = (
    shop,
    status
  ) => {

    axios

      .post(
        `${baseUrl}/admin/shops/updateKycStatus`,
        {
          id: shop.id,
          status: status,
        }
      )

      .then((res) => {

        if (!res.data.success) {

          Swal.fire({

            icon: "error",

            title: "Failed",

            text:
              res.data.message ||
              "Failed to update KYC.",

          });

          return;
        }


        // ====================================================
        // UPDATE LIST
        // ====================================================

        setShops((prev) =>

          prev.map((item) =>

            item.id === shop.id

              ? {
                  ...item,
                  kyc_status: status,

                  // When KYC approved,
                  // shop remains active.
                  ...(status === "approved"
                    ? {
                        status:
                          item.status === "blocked"
                            ? "blocked"
                            : "active",
                      }
                    : {}),
                }

              : item

          )

        );


        // ====================================================
        // UPDATE DETAIL
        // ====================================================

        setSelectedShop((prev) =>

          prev?.id === shop.id

            ? {
                ...prev,
                kyc_status: status,

                ...(status === "approved"
                  ? {
                      status:
                        prev.status === "blocked"
                          ? "blocked"
                          : "active",
                    }
                  : {}),
              }

            : prev

        );


        Swal.fire({

          icon:
            status === "approved"
              ? "success"
              : "error",

          title:
            status === "approved"
              ? "KYC Approved"
              : "KYC Rejected",

          text:
            `${shop.businessName} KYC has been ${status}.`,

          timer: 1800,

          showConfirmButton: false,

        });

      })

      .catch((error) => {

        console.log(
          "KYC Error:",
          error.response?.data
        );

        Swal.fire({

          icon: "error",

          title: "Error",

          text:
            error.response?.data?.message ||
            "Failed to update KYC.",

        });

      });
  };


  // ==========================================================
  // BLOCK SHOP
  // ==========================================================

  const handleBlockShop = (
    shop,
    reason
  ) => {

    axios

      .post(
        `${baseUrl}/admin/shops/updateStatus`,
        {
          id: shop.id,

          status: "blocked",

          reason: reason,
        }
      )

      .then((res) => {

        if (!res.data.success) {

          Swal.fire({

            icon: "error",

            title: "Block Failed",

            text:
              res.data.message ||
              "Failed to block shop.",

          });

          return;
        }


        // ====================================================
        // UPDATE LIST
        // ====================================================

        setShops((prev) =>

          prev.map((item) =>

            item.id === shop.id

              ? {
                  ...item,

                  status: "blocked",

                  block_reason: reason,
                }

              : item

          )

        );


        // ====================================================
        // UPDATE DETAIL
        // ====================================================

        setSelectedShop((prev) =>

          prev?.id === shop.id

            ? {
                ...prev,

                status: "blocked",

                block_reason: reason,
              }

            : prev

        );


        Swal.fire({

          icon: "success",

          title: "Shop Blocked",

          text:
            `${shop.businessName} has been blocked.`,

          confirmButtonColor: "#0f4c81",

        });

      })

      .catch((error) => {

        console.log(
          "Block Shop Error:",
          error.response?.data
        );

        Swal.fire({

          icon: "error",

          title: "Error",

          text:
            error.response?.data?.message ||
            "Unable to block shop.",

        });

      });
  };


  // ==========================================================
  // REACTIVATE SHOP
  // ==========================================================

  const handleReactivateShop = (
    shop
  ) => {

    axios

      .post(
        `${baseUrl}/admin/shops/updateStatus`,
        {
          id: shop.id,

          status: "active",
        }
      )

      .then((res) => {

        if (!res.data.success) {

          Swal.fire({

            icon: "error",

            title: "Reactivate Failed",

            text:
              res.data.message ||
              "Failed to reactivate shop.",

          });

          return;
        }


        // ====================================================
        // UPDATE LIST
        // ====================================================

        setShops((prev) =>

          prev.map((item) =>

            item.id === shop.id

              ? {
                  ...item,

                  status: "active",

                  block_reason: null,
                }

              : item

          )

        );


        // ====================================================
        // UPDATE DETAIL
        // ====================================================

        setSelectedShop((prev) =>

          prev?.id === shop.id

            ? {
                ...prev,

                status: "active",

                block_reason: null,
              }

            : prev

        );


        Swal.fire({

          icon: "success",

          title: "Shop Reactivated",

          text:
            `${shop.businessName} is active again.`,

          confirmButtonColor: "#0f4c81",

        });

      })

      .catch((error) => {

        console.log(
          "Reactivate Error:",
          error.response?.data
        );

        Swal.fire({

          icon: "error",

          title: "Error",

          text:
            error.response?.data?.message ||
            "Unable to reactivate shop.",

        });

      });
  };


  // ==========================================================
  // DETAIL VIEW
  // ==========================================================

  if (
    view === "detail" &&
    selectedShop
  ) {

    return (

      <ShopDetailPage

        shop={selectedShop}

        onBack={() =>
          setView("list")
        }

        onViewOrders={
          handleViewOrders
        }

        onApprove={(shop) =>
          handleKycStatusUpdate(
            shop,
            "approved"
          )
        }

        onReject={(shop) =>
          handleKycStatusUpdate(
            shop,
            "rejected"
          )
        }

        onBlockShop={
          handleBlockShop
        }

        onReactivateShop={
          handleReactivateShop
        }

      />

    );
  }


  // ==========================================================
  // ORDERS
  // ==========================================================

  if (
    view === "orders" &&
    selectedShop
  ) {

    return (

      <OrdersListPage

        shop={selectedShop}

        onBack={() =>
          setView("detail")
        }

      />

    );
  }


  // ==========================================================
  // LIST
  // ==========================================================

  return (

    <ShopListPage

      shops={shops}

      loading={loading}

      onViewShop={
        handleViewShop
      }

      onViewOrders={
        handleViewOrders
      }

      onApprove={(shop) =>
        handleKycStatusUpdate(
          shop,
          "approved"
        )
      }

      onReject={(shop) =>
        handleKycStatusUpdate(
          shop,
          "rejected"
        )
      }

      onBlockShop={
        handleBlockShop
      }

      onReactivateShop={
        handleReactivateShop
      }

    />

  );
};


export default ShopManagementInAdmin;